import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as sync from '@/services/api/channelSync.api';
import { fetchParityRollup } from '@/services/api/adminRateParity.api';
import type {
  SyncSnapshot,
  SyncSnapshotQuery,
  ChangeLogQuery,
  CellDetailQuery,
  VerifySyncBody,
  PushSyncCellBody,
} from '@/services/api/channelSync.api';

// ---------------------------------------------------------------------------
// react-query hooks for the AxisRooms sync surfaces. Keys nest under the
// existing `['channels']` root so the broad `['channels']` invalidations fired
// by useSyncConnection / usePushRate also refresh these views, and verify/push
// here invalidate `['channels','sync']` so every sync surface repaints together.
// ---------------------------------------------------------------------------

const ROOT = ['channels', 'sync'] as const;

/** E1 — snapshot (grid, calendar overlay, report table). */
export function useSyncSnapshot(q: SyncSnapshotQuery, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'snapshot', q],
    queryFn: () => sync.fetchSyncSnapshot(q),
    enabled: enabled && !!q.propertyId,
    staleTime: 30_000,
  });
}

/** E3 — change log (reverse-chron external-change / push events). */
export function useChangeLog(q: ChangeLogQuery, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'change-log', q],
    queryFn: () => sync.fetchChangeLog(q),
    enabled: enabled && !!q.propertyId,
    staleTime: 30_000,
  });
}

/** E4 — per-cell drill (on-demand, no cache). */
export function useCellDetail(q: CellDetailQuery | null, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'cell', q],
    queryFn: () => sync.fetchCellDetail(q as CellDetailQuery),
    enabled: enabled && !!q && !!q.propertyId && !!q.unitId,
    staleTime: 0,
  });
}

/** E6 — header status pill (cheap, polled). */
export function useStatusPill(propertyId: string, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'status-pill', propertyId],
    queryFn: () => sync.fetchStatusPill(propertyId),
    enabled: enabled && !!propertyId,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** E2 — [Sync now] pull-only verify. Invalidates every sync view. */
export function useVerifySync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VerifySyncBody) => sync.verifySync(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

/**
 * E5 — [Edit & push] the single AxisRooms-write. Optimistically flips the
 * touched cells to `push_unconfirmed` across cached snapshots; the confirming
 * (echo-suppressed) pull settles them to `synced`. Rolls back on error.
 */
export function usePushSyncCell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PushSyncCellBody) => sync.pushSyncCell(body),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: [...ROOT, 'snapshot'] });
      const previous = qc.getQueriesData<SyncSnapshot>({ queryKey: [...ROOT, 'snapshot'] });
      const inWindow = (date: string) => date >= vars.from.slice(0, 10) && date < vars.to.slice(0, 10);
      for (const [key, snap] of previous) {
        if (!snap) continue;
        qc.setQueryData<SyncSnapshot>(key, {
          ...snap,
          cells: snap.cells.map((c) =>
            c.unitId === vars.unitId && c.rateplanId === vars.rateplanId && inWindow(c.date)
              ? { ...c, parityState: 'push_unconfirmed' as const }
              : c,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, snap] of ctx?.previous ?? []) {
        qc.setQueryData(key, snap);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
      // Overview / status cards also reflect a push.
      void qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

/** E8 — SUPER_ADMIN cross-property rollup (platform console). */
export function useAdminParityRollup(enabled = true) {
  return useQuery({
    queryKey: ['admin-channels', 'parity'],
    queryFn: () => fetchParityRollup(),
    enabled,
    staleTime: 30_000,
  });
}
